import _ from "lodash";
import React from "react";
import moment from "moment";
import DATE_FORMATS from "../constants/dateFormats";
import ExternalLink from "../../report/common/components/ExternalLink";
import config from "../../../core/config/configureSettings";

export function date(row, format = DATE_FORMATS.date) {
  if (!row.value) {
    return null;
  }

  const momentDate = moment(row.value);

  if (!momentDate.isValid()) {
    return row.value;
  }
  return momentDate.format(format);
}

export function dateTime(row, format = DATE_FORMATS.dateTime) {
  return date(row, format);
}

export function dateTimeWithSecond(row, format = DATE_FORMATS.dateTimeSec) {
  return date(row, format);
}

export function dateTimeWithUtc(row, format = DATE_FORMATS.dateTime) {
  if (!row.value) {
    return null;
  }

  const utcValue = row.value.split("(").pop();
  return `${date(row, format)} (${utcValue}`;
}

export function percentageWithoutSign(row) {
  const percentageValue = row.value;

  if (
    typeof percentageValue === "undefined" ||
    Number.isNaN(Number(percentageValue)) ||
    percentageValue === null
  ) {
    return "";
  }

  return Number(Number(percentageValue).toFixed(1));
}

export function percentage(row) {
  const percentageValue = row.value;

  if (
    typeof percentageValue === "undefined" ||
    Number.isNaN(Number(percentageValue)) ||
    percentageValue === null
  ) {
    return "";
  }

  return `${Number(percentageValue).toFixed(1)} %`;
}

export function numberNumber(row) {
  return typeof row.value === "undefined" ||
  row.value === null ||
  Number.isNaN(Number(row.value))
    ? null
    : row.value;
}

export function number(row) {
  return typeof row.value === "undefined" ||
    row.value === null ||
    Number.isNaN(Number(row.value))
    ? ""
    : row.value.toString();
}

export function float(row) {
  if (typeof row.value === "undefined" || row.value === null) {
    return "";
  }
  const numberString = number(row);
  return numberString.indexOf(".") >= 0 ? numberString : `${numberString}.0`;
}

export function millisecondToSecond(row) {
  if (
    typeof row.value === "undefined" ||
    row.value === null ||
    Number.isNaN(Number(row.value))
  ) {
    return "";
  }
  const numberString = (row.value / 1000).toString();
  return numberString.indexOf(".") >= 0 ? numberString : `${numberString}.0`;
}

export function boolean(row) {
  return row.value && "Y";
}

export function duration(row) {
  if (typeof row.value === "undefined" || row.value === null) {
    return "";
  }
  const hours = parseInt(_.first(row.value.split(":")), 10);
  const minutes = parseInt(_.last(row.value.split(":")), 10);

  return `${`${hours}`.padStart(2, "0")}:${`${minutes}`.padStart(2, "0")}`;
}

export function durationWithDay(row) {
  if (typeof row.value === "undefined" || row.value === null) {
    return "";
  }
  let hours;
  let minutes;
  let day = "";
  hours = parseInt(_.first(row.value.split(":")), 10);
  minutes = parseInt(_.last(row.value.split(":")), 10);
  const dayFormat = row.value.includes("d ");
  if (dayFormat) {
    day = _.first(row.value.split(" "));
    const removedDay = _.last(row.value.split("d "));
    hours = parseInt(_.first(removedDay.split(":")), 10);
    minutes = parseInt(_.last(removedDay.split(":")), 10);
  }
  return `${day} ${`${hours}`.padStart(2, "0")}:${`${minutes}`.padStart(
    2,
    "0"
  )}`;
}

export function multiline(row, isHtml = true) {
  let values = row.value || [];
  if (Array.isArray(values) === false) {
    values = [row.value];
  }
  return values.map((value, i) =>
    isHtml ? (
      <div key={i} className="ellipsis multiline-item">
        {value}
      </div>
    ) : (
      value
    )
  ); // eslint-disable-line react/no-array-index-key
}

export function multilineDate(row, format = DATE_FORMATS.date, isHtml = true) {
  let values = row.value || [];
  if (Array.isArray(values) === false) {
    values = [row.value];
  }
  return values.map((value, i) => {
    const formattedValue = moment(value).isValid()
      ? moment(value).format(format)
      : value;
    // eslint-disable-next-line react/no-array-index-key
    return isHtml ? (
      <div key={i} className="ellipsis multiline-item">
        {formattedValue}
      </div>
    ) : (
      formattedValue
    );
  });
}

export function multilineDateTime(
  row,
  format = DATE_FORMATS.dateTime,
  isHtml = true
) {
  return multilineDate(row, format, isHtml);
}

export function multilineReceptacleLink(row, isHtml = true) {
  const { atatSystem } = config.applications;
  const host = atatSystem;
  let values = row.value || [];
  if (Array.isArray(values) === false) {
    values = [row.value];
  }
  // eslint-disable-next-line react/no-array-index-key
  return values.map((value, i) =>
    isHtml ? (
      <div key={i} className="ellipsis-link multiline-item">
        <ExternalLink
          host={host}
          label={value}
          path={`/en/receptacle/${value}`}
        />
      </div>
    ) : (
      value
    )
  );
}

export function multilinePercentage(row, isHtml = true) {
  let values = row.value || [];
  if (Array.isArray(values) === false) {
    values = [row.value];
  }
  return values.map((value, i) => {
    const formattedValue = percentage({ value });
    // eslint-disable-next-line react/no-array-index-key
    return isHtml ? (
      <div key={i} className="ellipsis multiline-item">
        {formattedValue}
      </div>
    ) : (
      formattedValue
    );
  });
}

export function multilineNumber(row, isHtml = true) {
  let values = row.value || [];
  if (Array.isArray(values) === false) {
    values = [row.value];
  }
  return values.map((value, i) => {
    const formattedValue = number({ value });
    // eslint-disable-next-line react/no-array-index-key
    return isHtml ? (
      <div key={i} className="ellipsis multiline-item">
        {formattedValue}
      </div>
    ) : (
      formattedValue
    );
  });
}

export function nonZeroNumber(row) {
  return row && row.value ? number(row) : "";
}

export function nonZeroNumberDurationSecond(row) {
  return row && row.value ? millisecondToSecond(row) : "";
}

export function nonZeroPercentage(row) {
  return row && row.value ? percentage(row) : "";
}

export function nonZeroFloat(row) {
  return row && row.value ? float(row) : "";
}

export function nonZeroText(row) {
  return row && row.value && row.value !== "0" ? row.value.toString() : "";
}

// ============================================================================
// EXCEL-SPECIFIC FORMATTERS
// ============================================================================
export function excelPercentageAsDecimal(row) {
  const percentageValue = row.value;

  if (
    typeof percentageValue === "undefined" ||
    Number.isNaN(Number(percentageValue)) ||
    percentageValue === null ||
    percentageValue === ""
  ) {
    return "";
  }

  return Number(percentageValue.toFixed(1)) / 100;
}

export function excelCurrency(row) {
  if (
    row.value == null ||
    row.value === "" ||
    Number.isNaN(Number(row.value))
  ) {
    return "";
  }
  return Number(row.value);
}

export function excelText(row) {
  if (row.value == null || row.value === "") {
    return "";
  }
  if (typeof row.value === "object" && row.value.text) {
    return row.value.text.toString();
  }
  if (typeof row.value === "object" && row.value.label) {
    return row.value.label.toString();
  }
  return row.value.toString();
}

// ============================================================================
// END EXCEL-SPECIFIC FORMATTERS
// ============================================================================

export const formatMap = {
  // ============================================
  // Standard formatters (for web display)
  // ============================================
  date,
  dateTime,
  dateTimeWithSecond,
  dateTimeWithUtc,
  float,
  percentage,
  percentageWithoutSign,
  boolean,
  number,
  duration,
  durationWithDay,
  multiline,
  multilineDate,
  multilineDateTime,
  multilineReceptacleLink,
  multilinePercentage,
  multilineNumber,
  nonZeroNumber,
  nonZeroNumberDurationSecond,
  nonZeroPercentage,
  nonZeroFloat,
  nonZeroText,

  // ============================================
  // 📊 Excel-specific formatters
  // ============================================
  excelPercentageAsDecimal,
  excelCurrency,
  excelText,
};

export default function formatField(fieldType) {
  return formatMap[fieldType];
}
